import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Lock } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { BackToLoginLink } from '@/components/auth/BackToLoginLink'
import { AuthenticationLayout } from '@/components/layout/AuthenticationLayout.tsx'
import { cn } from '@/lib/utils'

type ForgotPasswordFormData = {
  email: string
}

interface ForgotPasswordFormProps {
  onSubmit: (data: ForgotPasswordFormData) => Promise<void>
  isLoading?: boolean
  error?: string
}

export function ForgotPasswordForm({
  onSubmit,
  isLoading = false,
  error,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation()

  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.emailInvalid')),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  return (
    <AuthenticationLayout
      icon={<Lock className='w-6 h-6 text-dark-600' />}
      title={t('auth.forgotPasswordTitle')}
      description={t('auth.forgotPasswordDescription')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        <div className='space-y-2'>
          <Text
            as='label'
            htmlFor='email'
            className='text-sm font-normal'
            variant='formLabel'
          >
            {t('common.email')}
          </Text>
          <Input
            id='email'
            type='text'
            placeholder={t('auth.enterEmail')}
            className={cn(
              'h-11 bg-white',
              (errors.email || error) && 'border-red-500',
              !(errors.email || error) && 'border-dark-300'
            )}
            {...register('email')}
            disabled={isLoading}
          />
          {errors.email && (
            <p className='text-sm text-red-600'>{errors.email.message}</p>
          )}
        </div>

        <Button
          type='submit'
          className='w-full h-11 font-medium'
          disabled={isLoading}
        >
          {t('auth.sendResetLink')}
        </Button>

        <BackToLoginLink />
      </form>
    </AuthenticationLayout>
  )
}
